import { useAuth } from "@clerk/clerk-react";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import BentoBrandsAutocomplete from "components/BentoBrandsAutocomplete";
import { fetcherAuth } from "utils/api";

import styles from "../../styles";

interface DoNotRecommendBrand {
  id: number;
  brandName: string;
}

export interface DoNotRecommendHandle {
  /** Persists staged changes; returns the names that couldn't be matched to a brand. */
  save: () => Promise<string[]>;
}

interface DoNotRecommendProps {
  setIsDirty?: (isDirty: boolean) => void;
}

const DoNotRecommend = forwardRef<DoNotRecommendHandle, DoNotRecommendProps>(
  ({ setIsDirty }, ref) => {
    const { getToken } = useAuth();
    const { currentOrg } = useContext(OrganizationUserContext);
    const { setAlert } = useContext(AlertContext);

    const [savedBrands, setSavedBrands] = useState<DoNotRecommendBrand[]>([]);
    const [listLoading, setListLoading] = useState(true);

    const [addedNames, setAddedNames] = useState<string[]>([]);
    const [removedIds, setRemovedIds] = useState<number[]>([]);

    useEffect(() => {
      if (currentOrg?.id) fetchList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrg?.id]);

    const fetchList = async () => {
      setListLoading(true);
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/brand-recommendations/do-not-recommend`,
          "GET",
        );
        setSavedBrands(res?.brands || []);
      } catch (error) {
        setAlert(
          error?.message || "Unable to load your do-not-recommend list",
          "warning",
        );
      } finally {
        setListLoading(false);
      }
    };

    const stageAdds = (names: string[]) => {
      setAddedNames(names);
      if (setIsDirty) setIsDirty(true);
    };

    const stageRemove = (brand: DoNotRecommendBrand) => {
      setRemovedIds((prev) => [...prev, brand.id]);
      if (setIsDirty) setIsDirty(true);
    };

    const save = async (): Promise<string[]> => {
      const names = addedNames.map((n) => n.trim()).filter(Boolean);
      if (names.length === 0 && removedIds.length === 0) return [];

      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/brand-recommendations/do-not-recommend`,
        "POST",
        {},
        { addNames: names, removeBrandIds: removedIds },
      );

      setSavedBrands(res?.brands || []);
      setAddedNames([]);
      setRemovedIds([]);
      return res?.unmatched || [];
    };

    useImperativeHandle(ref, () => ({ save }));

    const visibleBrands = savedBrands.filter(
      (brand) => !removedIds.includes(brand.id),
    );

    return (
      <Box sx={styles.questionBox}>
        <Typography gutterBottom>Brands to never recommend or email</Typography>
        <Typography variant="caption" color="textSecondary" paragraph>
          We'll keep these out of your recommendations until you remove them
          here. Changes are saved when you save your profile.
        </Typography>

        <BentoBrandsAutocomplete
          selectedBentoBrands={addedNames}
          updateSelectedBentoBrands={stageAdds}
        />

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Brands you've hidden
          </Typography>
          {listLoading ? (
            <CircularProgress size={24} />
          ) : visibleBrands.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              You haven't hidden any brands yet.
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {visibleBrands.map((brand) => (
                <Chip
                  key={brand.id}
                  label={brand.brandName}
                  onDelete={() => stageRemove(brand)}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    );
  },
);

export default DoNotRecommend;
